import { debounce } from '@mui/material';
import { CreateJobInvoiceBody } from 'api/invoices/invoices.types';
import _ from 'lodash';
import {
  CorrectiveInvoice,
  Counterparty,
  CurrencyCode,
  Invoice,
  InvoiceClass,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { Noop } from 'react-hook-form';
import { addDaysToDate, dateStringToEpoch } from 'utils/dateFunctions.utils';
import { ensureObjectOrNull } from 'utils/encoders';
import {
  isForeignInvoice,
  isInvoiceCorrective,
  isInvoiceInternal,
  recalculateTotals,
} from 'utils/invoiceFunctions.utils';
import { toFixedCurrency } from 'utils/numberFunctions.utils';
import { FormInputs, __InvoiceType } from './types';

export const isCorrectedInvoiceFieldVisible = (code: string): boolean =>
  code === 'FKS/A' || code === 'FNPKS' || code === 'FK' || code === 'FKS';

export const isCodeFieldVisible = (code: string): boolean =>
  code === 'FK' || code === 'FZ';

export const fieldIsRequired = {
  required: true,
  validate: (v: any) => !_.isEmpty(v),
};

export const getInvoiceCounterpartyValidationError = (
  invoiceClassCode?: string,
  countryCode?: string,
  jobTradeType?: string,
): 'exportVatForeignCounterparty' | 'noVatPolishCounterparty' | null => {
  if (!countryCode) return null;

  if (invoiceClassCode === 'FNPS' && countryCode === 'PL') {
    return 'noVatPolishCounterparty';
  }

  if (
    invoiceClassCode === 'FVS' &&
    jobTradeType === 'EXPORT' &&
    countryCode !== 'PL'
  ) {
    return 'exportVatForeignCounterparty';
  }

  return null;
};

export const createHandleOnBlur =
  (
    setOptions: React.Dispatch<React.SetStateAction<any>>,
    setParams: React.Dispatch<React.SetStateAction<any>>,
    onBlur: Noop,
  ) =>
  () => {
    onBlur();
    setParams((prev: object) => ({
      ...prev,
      filterText: '',
      pageNumber: 1,
    }));
    setOptions([]);
  };

export const handleChange = (
  setOptions: React.Dispatch<React.SetStateAction<any>>,
  setParams: React.Dispatch<React.SetStateAction<any>>,
) =>
  debounce((value: string) => {
    setOptions([]);
    setParams((prev: object) => ({
      ...prev,
      filterText: value,
      pageNumber: 1,
    }));
  }, 800);

export const getJobInvoiceRequestBody = (
  formData: any,
): CreateJobInvoiceBody => {
  const { counterparty, createdFrom, ...data } = formData;
  const payload: CreateJobInvoiceBody = {
    ...data,
    __type: 'Invoice',
    buyerCounterparty: {
      id: formData.counterparty.id,
    },
    clauses: data.clauses ? data.clauses : [],
    currencyCode: {
      id: formData.currencyCode.id,
    },
    dueDate:
      formData.dueDate === 'CAD'
        ? dateStringToEpoch(formData.issueDate!)
        : formData.dueDate === 'Przedpłata'
        ? dateStringToEpoch(addDaysToDate(formData.issueDate!, -1))
        : dateStringToEpoch(formData.dueDate!),
    exchangeRateDate: dateStringToEpoch(formData.exchangeRateDate),
    issueDate: dateStringToEpoch(formData.issueDate),
    receiptDate: formData?.receiptDate
      ? dateStringToEpoch(formData?.receiptDate)
      : dateStringToEpoch(formData.issueDate!),
    saleDate: dateStringToEpoch(formData.saleDate),
    sellerCounterparty: {
      id: isInvoiceInternal(formData.invoiceClass)
        ? 1
        : formData.sellerCounterparty.id,
    },
    invoiceLines: data.invoiceLines.map((invoiceLine: InvoiceLine) => {
      invoiceLine.jobItem = ensureObjectOrNull(invoiceLine.jobItem);
      const netUnitPrice = Number(
        toFixedCurrency(
          invoiceLine.currencyAmount.value * invoiceLine.exchangeRate,
          6,
        ),
      );
      invoiceLine.netUnitPriceInInvoiceCurrency = netUnitPrice;
      invoiceLine.amounts.netValue = Number(
        toFixedCurrency(invoiceLine.quantity * netUnitPrice, 2),
      );
      invoiceLine.amounts.taxValue = Number(
        toFixedCurrency(
          invoiceLine.amounts.netValue *
            (invoiceLine.amounts.taxRate.value / 100),
          2,
        ),
      );
      invoiceLine.amounts.grossValue = Number(
        toFixedCurrency(
          invoiceLine.amounts.netValue + invoiceLine.amounts.taxValue,
          2,
        ),
      );
      return invoiceLine;
    }),
  };
  return payload;
};

export const prepareCreateInvoiceRequestBody = (data: any) => {
  const { counterparty, correctedInvoice, code, ...restData } = data;
  const isCorrective = isInvoiceCorrective(restData.invoiceClass);

  const payload = {
    ...restData,
    __type: isCorrective
      ? __InvoiceType.CORRECTIVE_INVOICE
      : __InvoiceType.INVOICE,
    buyerCounterparty: {
      id: restData.invoiceClass.own ? (counterparty as Counterparty).id : 1,
    },
    clauses: restData.clauses || [],
    ...(!restData.invoiceClass.own && {
      code,
    }),
    ...((isCorrective && {
      correctedInvoiceCode: (correctedInvoice as Invoice).code,
      correctedInvoiceLines: [],
      generated: true,
      previousInvoiceLines: (correctedInvoice as Invoice).invoiceLines,
      previousTaxTotals: (correctedInvoice as any).taxTotals,
      previousTotals: (correctedInvoice as Invoice).totals,
    }) ||
      {}),
    sellerCounterparty: {
      id:
        restData.invoiceClass.own || isInvoiceInternal(restData.invoiceClass)
          ? 1
          : (counterparty as Counterparty).id,
    },
    currencyCode: { id: (restData.currencyCode as CurrencyCode).id },
    dueDate:
      restData.dueDate === 'CAD'
        ? dateStringToEpoch(restData.issueDate!)
        : restData.dueDate === 'Przedpłata'
        ? dateStringToEpoch(addDaysToDate(restData.issueDate!, -1))
        : dateStringToEpoch(restData.dueDate!),
    exchangeRateDate: dateStringToEpoch(restData.exchangeRateDate!),
    ...(!isCorrective && {
      invoiceLines: [],
      requiresCorrection: false,
    }),
    issueDate: dateStringToEpoch(restData.issueDate!),
    owner: null,
    receiptDate: restData?.receiptDate
      ? dateStringToEpoch(restData?.receiptDate)
      : dateStringToEpoch(restData.issueDate!),
    saleDate: dateStringToEpoch(restData.saleDate!),
    taxTotals: [],
    totals: {
      grossValue: 0,
      netValue: 0,
      netValueAllocationSum: 0,
      taxValue: 0,
    },
  };

  return payload;
};

export const prepareEditInvoiceRequestBody = (
  { counterparty, ...formData }: FormInputs,
  { createdFrom, ...invoiceData }: any,
) => {
  const isCorrective = isInvoiceCorrective(
    invoiceData?.invoiceClass as InvoiceClass,
  );
  const isForeign = isForeignInvoice(invoiceData?.invoiceClass as InvoiceClass);

  const payload = {
    ...invoiceData,
    ...formData,
    clauses: formData.clauses ? formData.clauses : invoiceData?.clauses,
    buyerCounterparty: {
      id: isForeign
        ? invoiceData?.buyerCounterparty.id
        : (counterparty as Counterparty).id,
    },
    sellerCounterparty: {
      id: isInvoiceInternal(invoiceData?.invoiceClass)
        ? 1
        : isForeign
        ? (counterparty as Counterparty).id
        : invoiceData?.sellerCounterparty.id,
    },
    owner: {
      id: invoiceData?.owner.id,
    },
    issueDate: dateStringToEpoch(formData.issueDate!),
    dueDate:
      formData.dueDate === 'CAD'
        ? dateStringToEpoch(formData.issueDate!)
        : formData.dueDate === 'Przedpłata'
        ? dateStringToEpoch(addDaysToDate(formData.issueDate!, -1))
        : dateStringToEpoch(formData.dueDate!),
    exchangeRateDate: dateStringToEpoch(formData.exchangeRateDate!),
    receiptDate: formData?.receiptDate
      ? dateStringToEpoch(formData?.receiptDate)
      : dateStringToEpoch(formData.issueDate!),
    saleDate: dateStringToEpoch(formData.saleDate!),
    __type: isCorrective
      ? __InvoiceType.CORRECTIVE_INVOICE
      : __InvoiceType.INVOICE,
  };
  const recalculatedPayload = !isCorrective
    ? recalculateTotals(payload)
    : payload;

  return recalculatedPayload;
};

export const prepareRemoveAllLinesBody = (
  invoiceData: Invoice | CorrectiveInvoice,
) => {
  const isCorrective = isInvoiceCorrective(
    invoiceData?.invoiceClass as InvoiceClass,
  );
  const { createdFrom, ...data } = invoiceData;
  const recalculatedData = !isCorrective
    ? recalculateTotals({ ...data, invoiceLines: [] })
    : recalculateTotals({
        ...(data as CorrectiveInvoice),
        correctedInvoiceLines: [],
      });

  const payload = {
    ...recalculatedData,
    __type: isCorrective
      ? __InvoiceType.CORRECTIVE_INVOICE
      : __InvoiceType.INVOICE,
  };

  return payload;
};
